/**
 * Keeps a file the user had already chosen across a Stripe round trip.
 *
 * An import stopped by a plan wall used to lose its file: checkout is a full
 * navigation, so the CSV or PDF sitting in component state was gone when the
 * user came back, and they uploaded it again (four times, in one traced case)
 * or did not. A `File` cannot go in localStorage, but it is structured-cloneable,
 * so IndexedDB holds it. Written when the user clicks Upgrade on a wall, read
 * once by the import surface they return to, and dropped after an hour.
 *
 * Every call swallows its errors: a private window or a blocked store means the
 * user picks the file again, which is exactly what happened before.
 */

const DB_NAME = "cm-pending-files";
const STORE = "files";
const TTL_MS = 60 * 60 * 1000;
/** Above this the stash is skipped rather than risk a quota error mid-click. */
const MAX_BYTES = 64 * 1024 * 1024;

export type PendingFileKey = "csv-import" | "pdf-import";

type Entry = { blob: Blob; name: string; type: string; ts: number };

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function run<T>(
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const request = action(tx.objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        tx.oncomplete = () => db.close();
      }),
  );
}

/** Store the file under `key`, replacing any earlier one. Resolves false when it could not. */
export async function stashPendingFile(
  key: PendingFileKey,
  file: Blob & { name?: string },
): Promise<boolean> {
  if (!file || file.size > MAX_BYTES) return false;
  const entry: Entry = {
    blob: file,
    name: file.name ?? key,
    type: file.type ?? "",
    ts: Date.now(),
  };
  try {
    await run("readwrite", (store) => store.put(entry, key));
    return true;
  } catch {
    return false;
  }
}

/**
 * One answer per key per page load. The import surfaces can mount more than once
 * while a page settles (Suspense, a parent re-keying), and the first mount's read
 * deletes the record while its own result is thrown away on unmount. Sharing the
 * promise gives every mount in that window the same file. Cleared shortly after,
 * so reopening the import later does not restore the file a second time.
 */
const inFlight = new Map<PendingFileKey, Promise<File | null>>();

/** Read and remove the file under `key`. Null when none, expired, or the store is unreachable. */
export function takePendingFile(key: PendingFileKey): Promise<File | null> {
  let pending = inFlight.get(key);
  if (!pending) {
    pending = readAndRemove(key);
    inFlight.set(key, pending);
    pending.finally(() => setTimeout(() => inFlight.delete(key), 30_000));
  }
  return pending;
}

async function readAndRemove(key: PendingFileKey): Promise<File | null> {
  try {
    const entry = await run<Entry | undefined>("readonly", (store) =>
      store.get(key),
    );
    if (!entry) return null;
    await run("readwrite", (store) => store.delete(key));
    if (Date.now() - entry.ts > TTL_MS) return null;
    return new File([entry.blob], entry.name, { type: entry.type });
  } catch {
    return null;
  }
}
